import { ProperElement } from '../react-components/Validator/Validator';
import EventService from '../react-components/CalculationComponents/calculationComponents.eventservice';
import { Field } from '../react-components/StatsbidragGlobals/statsbidrag.types';
import { Icon } from 'sos-common/js/components/Icon/icon';
import ReactDOMServer from 'react-dom/server';
import ReactDOM from 'react-dom';
import React from 'react';
import Console from 'sos-common/js/utils/console.log';

const ERROR_IS_REQUIRED = 'Dessa uppgifter är obligatoriska att fylla i';
const ERROR_INVALID_REGEX = 'Du har angivit fel format på uppgiften';

/**
 * Contains methods to validate different type of components.
 */
class ValidationService {
  private static instance: ValidationService;
  private _formElements: ProperElement[];

  private constructor() {
    this._formElements = null;
  }

  public static getInstance = (): ValidationService => {
    if (!ValidationService.instance) ValidationService.instance = new ValidationService();
    return ValidationService.instance;
  };

  public setFormElements = (elements: ProperElement[]) => {
    this._formElements = elements;
  };

  private getElementName = (element: HTMLElement) => {
    return element.getAttribute('name') !== null
      ? element.getAttribute('name')
      : element.getAttribute('data-f-element-name');
  };

  public validateComponent = (fields: Field[][], elementName: string) => {
    let componentIsValid = true;
    let fieldsClone = fields.slice();
    fieldsClone.forEach((fieldArr) => {
      fieldArr.forEach((field) => {
        if (field.mandatory && field.value === '') {
          field.isValid = false;
          componentIsValid = false;
        } else {
          field.isValid = true;
        }
      });
    });

    return { componentIsValid, fieldsClone };
  };

  public validateStandardComponent = (elementName: string) => {
    if (this._formElements) {
      let fieldCollection = this._formElements.filter((x) =>
        x.element.attributes.getNamedItem('name') !== null
          ? x.element.attributes.getNamedItem('name').value === elementName
          : x.element.attributes.getNamedItem('data-f-element-name').value === elementName
      );
      let validatedCollection = this.validateFieldCollection(fieldCollection);
      if (validatedCollection.length > 0) this.validateStep(validatedCollection[0].step);
    } else {
      Console.warn('No form elements have been set.');
    }
  };

  public validateStepViaElementName = (elementName: string) => {
    if (this._formElements) {
      let element = this._formElements.filter((x) =>
        x.element.attributes.getNamedItem('name') !== null
          ? x.element.attributes.getNamedItem('name').value === elementName
          : x.element.attributes.getNamedItem('data-f-element-name').value === elementName
      );
      this.validateStep(element[0].step);
    } else {
      Console.warn('No form elements have been set.');
    }
  };

  public validateStep = (step: number) => {
    if (this._formElements) {
      let stepIsValid = true;
      Console.log(`validating step: ${step}`);
      let collectionClone = this.validateFieldCollection(
        this._formElements.filter((x) => x.step === step),
        false
      );
      let firstErronousField = collectionClone.find((x) => x.isValid === false);
      if (firstErronousField !== undefined) {
        stepIsValid = false;
      }
      EventService.fire('step-is-validated', { detail: { step, stepIsValid } });
    } else {
      Console.warn('No form elements have been set.');
    }
  };

  public validateForm = (displayErrors = true) => {
    if (this._formElements) {
      let formIsValid = true;
      Console.log('validating form..');
      let collectionClone = this.validateFieldCollection(this._formElements, displayErrors);
      let firstErronousField = collectionClone.find((x) => x.isValid === false);
      if (firstErronousField !== undefined) {
        formIsValid = false;
      }
      EventService.fire('form-is-valid', { detail: formIsValid });
      return formIsValid;
    } else {
      Console.warn('No form elements have been set.');
    }
  };

  private validateFieldCollection = (collection: ProperElement[], displayErrors = true) => {
    let collectionClone = collection.slice();
    collection.forEach((properElement, index) => {
      // If there already is an error node, remove it first
      if (displayErrors) this.removeErrorMessage(properElement.element);

      let isValidAttr = properElement.element.getAttribute('data-isvalid');
      let elementIsValid = isValidAttr === 'true';

      // Custom component is invalid
      if (isValidAttr === 'false') {
        collectionClone[index].isValid = false;
        return;
      }
      // Custom component is valid
      if (isValidAttr === 'true') {
        collectionClone[index].isValid = true;
        return;
      }

      // Standard epi fields do not have data-isvalid attribute
      if (isValidAttr === null) {
        //let isReq = properElement.element.hasAttribute('required');
        let isReq = properElement.isRequired;
        let value: string | boolean = (properElement.element as HTMLInputElement).value;
        let regex = properElement.regex;

        // Checkboxes and radios need special logic
        if (properElement.type === 'choice') {
          value = false;
          properElement.elementGroup.forEach((choice) => {
            if ((choice as HTMLInputElement).checked) value = true;
          });
        }

        // No value and required
        if (!value && isReq) {
          collectionClone[index].isValid = false;
          if (displayErrors) this.injectErrorMessage(properElement.element, ERROR_IS_REQUIRED);
          return;
        }

        // Is not required, do not care about value
        if (!isReq) {
          collectionClone[index].isValid = true;
        }

        // Has value, has regex rules
        if (value && regex) {
          collectionClone[index].isValid = new RegExp(regex).test(value);
          if (!collectionClone[index].isValid) {
            if (displayErrors) this.injectErrorMessage(properElement.element, ERROR_INVALID_REGEX);
          }
          return;
        }

        // Has value, is required has no regex
        if (value && isReq && !regex) collectionClone[index].isValid = true;

        // Has no value, is not required
        if (!value && !isReq) collectionClone[index].isValid = true;

        // Element is valid, delete potential error message
        if (collectionClone[index].isValid) {
          if (displayErrors) this.removeErrorMessage(properElement.element);
        }
      }
    });
    return collectionClone;
  };

  private removeErrorMessage = (element: HTMLElement) => {
    element.classList.remove('has-error');
    let errorNode = document.getElementById(this.getElementName(element) + '-error');
    if (errorNode) errorNode.remove();
    element.removeAttribute('aria-labelledby');
  };

  private injectErrorMessage = (element: HTMLElement, error: string) => {
    var errorSpan = document.createElement('span');
    //element.id = `${this.getElementName(element)}-error`;
    const errorId = `${this.getElementName(element)}-error`;
    element.classList.add('has-error');
    element.setAttribute('aria-labelledby', errorId);
    element.insertAdjacentElement('afterend', errorSpan);
    ReactDOM.render(
      <span id={errorId} className="error-message" role="alert">
        <Icon iconType="Summering_inte_OK25px25px" />
        <span>{error}</span>
      </span>,
      errorSpan
    );
    errorSpan.replaceWith(...errorSpan.childNodes);
  };
}

export default ValidationService.getInstance();
